import React, { useState, useCallback, useEffect } from 'react';
import { Row, Col, message } from 'antd';
import { HomeOutlined, ShopOutlined } from '@ant-design/icons';
import CustomButton from '../../../../../../../../../v2-components/Antd/Button';
import CustomSelect from '../../../../../../../../../v2-components/Antd/Select';
import CustomInput from '../../../../../../../../../v2-components/Antd/Input';
import CustomRadio from '../../../../../../../../../v2-components/Antd/Radio';
import CustomCheckbox from '../../../../../../../../../v2-components/Antd/Checkbox';
import GoogleMapComponent from '../../components/GoogleMapComponent';
import { Service } from '../../../../../../../service';
import {
  expandStreetAbbreviations,
  hasTypology,
  isUsableBackendGeocode,
  pickMatchingResult,
  splitStreetNumber,
} from './addressValidation';
import './style.scss';

const HOUSING_TYPES = [
  { label: 'Casa', value: 'house' },
  { label: 'Departamento', value: 'apartment' },
];

export default function Step2DeliveryType({
  deliveryType,
  addressData,
  onDeliveryTypeChange,
  onAddressDataChange,
  onBack,
  onNext,
  isLoading = false,
}) {
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [isLoadingCommunes, setIsLoadingCommunes] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Load regions
  useEffect(() => {
    const fetchRegions = async () => {
      setIsLoadingRegions(true);
      try {
        const response = await new Service().getRegionsByCountry();
        console.log('Regions API response:', response);
        // Response is array of strings - keep value as-is for API compatibility
        const formattedRegions = (response || []).map(region => ({
          label: typeof region === 'string' ? region.trim() : region.name?.trim(),
          value: typeof region === 'string' ? region : (region.id || region.code || region.name),
        }));
        setRegions(formattedRegions);
      } catch (error) {
        console.error('Error fetching regions:', error);
        message.error('Error al cargar regiones');
      } finally {
        setIsLoadingRegions(false);
      }
    };
    
    if (deliveryType === 'delivery') {
      fetchRegions();
    }
  }, [deliveryType]);

  // Load communes when region changes
  useEffect(() => {
    const fetchCommunes = async () => {
      if (!addressData.region) {
        setCommunes([]);
        return;
      }
      
      setIsLoadingCommunes(true);
      try {
        // Send region as-is (without trim) for API compatibility
        const regionParam = addressData.region;
        const response = await new Service().getCommunesByRegion(regionParam);
        // Response could be array of strings or objects - keep value as-is, trim only label
        const formattedCommunes = (response || []).map(commune => ({
          label: typeof commune === 'string' ? commune.trim() : commune.name?.trim(),
          value: typeof commune === 'string' ? commune : (commune.id || commune.code || commune.name),
        }));
        setCommunes(formattedCommunes);
      } catch (error) {
        console.error('Error fetching communes - Full error:', error);
        console.error('Error response:', error?.response);
        message.error('Error al cargar comunas');
      } finally {
        setIsLoadingCommunes(false);
      }
    };
    
    fetchCommunes();
  }, [addressData.region]);

  const handleRegionChange = (value) => {
    const selectedRegion = regions.find(r => r.value === value);
    onAddressDataChange({
      region: value,
      regionName: selectedRegion?.label || '',
      commune: null,
      communeName: '',
      isValidated: false,
    });
  };

  const handleCommuneChange = (value) => {
    const selectedCommune = communes.find(c => c.value === value);
    onAddressDataChange({
      commune: value,
      communeName: selectedCommune?.label || '',
      isValidated: false,
    });
  };

  const handleInputChange = (field, value) => {
    onAddressDataChange({
      [field]: value,
      isValidated: false,
    });
  };

  const handleValidateAddress = useCallback(async () => {
    if (!addressData.region || !addressData.commune || !addressData.street) {
      message.warning('Por favor complete todos los campos de dirección');
      return;
    }

    if (!addressData.noNumber) {
      const hasNumber = /\d/.test(addressData.street);
      if (!hasNumber) {
        message.error('La dirección debe incluir el número de calle o marcar "Sin número"');
        return;
      }
    }

    setIsValidating(true);
    try {
      if (!window.google?.maps?.Geocoder) {
        message.error('Google Maps no está disponible');
        setIsValidating(false);
        return;
      }

      const enteredStreet = addressData.street;
      const expandedStreet = expandStreetAbbreviations(enteredStreet);
      const geocoder = new window.google.maps.Geocoder();

      const geocodeGoogle = (street) =>
        new Promise((resolve) => {
          const streetQuery = addressData.noNumber ? `${street} S/N` : street;
          const query = `${streetQuery}, ${addressData.communeName}, ${addressData.regionName}, Chile`;
          geocoder.geocode({ address: query }, (results, status) => {
            resolve(status === 'OK' && results ? results : []);
          });
        });

      // 1) Google: pick among ALL results by route name; prefer commune match, else precise street pin.
      let match = pickMatchingResult(
        await geocodeGoogle(expandedStreet),
        enteredStreet,
        addressData.communeName,
        addressData.noNumber,
      );

      // 2) Retry with "Calle" when the street name collides with a locality (Santiago, Penco).
      if (!match && !hasTypology(expandedStreet)) {
        match = pickMatchingResult(
          await geocodeGoogle(`Calle ${expandedStreet}`),
          enteredStreet,
          addressData.communeName,
          addressData.noNumber,
        );
      }

      let coordinates = null;

      if (match) {
        const location = match.geometry.location;
        coordinates = { lat: location.lat(), lng: location.lng() };
      } else {
        // 3) Backend fallback — same endpoint as demo AddressSetLocation, but reject city-only
        // responses like "Valparaíso, Chile" (those painted Pedro Montt / Av. Francia).
        const backendQuery = `${addressData.regionName}, ${addressData.communeName}, ${expandedStreet}`;
        try {
          const backend = await new Service().geocodeAddress(backendQuery);
          if (
            backend?.lat &&
            backend?.lng &&
            isUsableBackendGeocode(backend.address, enteredStreet)
          ) {
            coordinates = { lat: backend.lat, lng: backend.lng };
          }
        } catch (backendError) {
          console.error('Backend geocode fallback failed:', backendError);
        }
      }

      console.log('Address validation:', { match, coordinates });

      if (!coordinates) {
        message.error('No pudimos ubicar esa calle. Verifique el nombre y el número.');
        onAddressDataChange({ isValidated: false, validatedAddress: null, coordinates: null });
        setIsValidating(false);
        return;
      }

      const { street: parsedStreet, number: parsedNumber } = splitStreetNumber(enteredStreet);

      onAddressDataChange({
        isValidated: true,
        validatedAddress: `${enteredStreet}, ${addressData.communeName}, ${addressData.regionName}`,
        street: addressData.noNumber ? enteredStreet : parsedStreet,
        streetNumber: addressData.noNumber ? 'S/N' : parsedNumber,
        coordinates,
      });

      message.success('Dirección validada correctamente');
      setIsValidating(false);
    } catch (error) {
      console.error('Error validating address:', error);
      message.error('Error al validar la dirección');
      setIsValidating(false);
    }
  }, [addressData, onAddressDataChange]);

  const canContinue = deliveryType === 'pickup' || 
    (deliveryType === 'delivery' && addressData.isValidated);

  return (
    <div className="step-delivery-type">
      <div className="step-header">
        <h2>Finalizar compra</h2>
        <p className="step-subtitle">Seleccionar tipo de entrega</p>
      </div>

      <div className="delivery-options">
        <Row gutter={16}>
          <Col span={12}>
            <div 
              className={`delivery-card ${deliveryType === 'delivery' ? 'selected' : ''}`}
              onClick={() => onDeliveryTypeChange('delivery')}
            >
              <div className="card-content">
                <HomeOutlined className="card-icon" />
                <span className="card-label">Envío a domicilio</span>
              </div>
              <CustomRadio
                checked={deliveryType === 'delivery'}
                onChange={() => onDeliveryTypeChange('delivery')}
              />
            </div>
          </Col>
          <Col span={12}>
            <div 
              className={`delivery-card ${deliveryType === 'pickup' ? 'selected' : ''}`}
              onClick={() => onDeliveryTypeChange('pickup')}
            >
              <div className="card-content">
                <ShopOutlined className="card-icon" />
                <span className="card-label">Retiro en farmacia</span>
              </div>
              <CustomRadio
                checked={deliveryType === 'pickup'}
                onChange={() => onDeliveryTypeChange('pickup')}
              />
            </div>
          </Col>
        </Row>
      </div>

      {deliveryType === 'delivery' && (
        <div className="address-section">
          <h3 className="address-title">Datos para la entrega</h3>
          
          <div className="map-container">
            <GoogleMapComponent
              coordinates={addressData.coordinates}
              validatedAddress={addressData.validatedAddress}
            />
          </div>

          <div className="address-form">
            <Row gutter={16}>
              <Col span={12}>
                <div className="form-field">
                  <label>Región</label>
                  <CustomSelect
                    value={addressData.region}
                    onChange={handleRegionChange}
                    options={regions}
                    loading={isLoadingRegions}
                    placeholder="Seleccionar región"
                    showSearch
                    style={{ width: '100%' }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div className="form-field">
                  <label>Comuna</label>
                  <CustomSelect
                    value={addressData.commune}
                    onChange={handleCommuneChange}
                    options={communes}
                    loading={isLoadingCommunes}
                    disabled={!addressData.region}
                    placeholder="Seleccionar comuna"
                    showSearch
                    style={{ width: '100%' }}
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <div className="form-field">
                  <label>Calle y número</label>
                  <CustomInput
                    value={addressData.street}
                    onChange={(e) => handleInputChange('street', e.target.value)}
                    placeholder="Ej: Av. Providencia 1234"
                  />
                    <div className="checkbox-container">                    
                      <CustomCheckbox 
                      checked={addressData.noNumber} 
                      onChange={(e) => handleInputChange('noNumber', e.target.checked)}
                    >
                      Esta dirección no tiene número
                    </CustomCheckbox>
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <div className="form-field">
                  <label>Tipo de vivienda</label>
                  <CustomSelect
                    value={addressData.housingType}
                    onChange={(value) => handleInputChange('housingType', value)}
                    options={HOUSING_TYPES}
                    placeholder="Seleccionar"
                    style={{ width: '100%' }}
                  />
                </div>
              </Col>
              {addressData.housingType === 'apartment' && (
                <Col span={8}>
                  <div className="form-field">
                    <label>Piso/Depto/Torre</label>
                    <CustomInput
                      value={addressData.floorDeptTower}
                      onChange={(e) => handleInputChange('floorDeptTower', e.target.value)}
                      placeholder="Ej: Piso 5, Depto 502"
                    />
                  </div>
                </Col>
              )}
              <Col span={addressData.housingType === 'apartment' ? 8 : 16}>
                <div className="form-field">
                  <label>Referencia</label>
                  <CustomInput
                    value={addressData.reference}
                    onChange={(e) => handleInputChange('reference', e.target.value)}
                    placeholder="Ej: Frente al parque"
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <CustomButton
                  type="default"
                  onClick={handleValidateAddress}
                  loading={isValidating}
                  className="btn-validate"
                  disabled={!addressData.region || !addressData.commune || !addressData.street}
                >
                  Validar dirección
                </CustomButton>
              </Col>
            </Row>

            {addressData.isValidated && addressData.validatedAddress && (
              <div className="validated-address">
                <span className="validation-icon">✓</span>
                <span className="validated-text">
                  Dirección validada: {addressData.validatedAddress}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="step-footer">
        <CustomButton onClick={onBack} className="btn-back">
          Volver
        </CustomButton>
        <CustomButton
          type="primary"
          onClick={onNext}
          disabled={!canContinue}
          loading={isLoading}
          className="btn-continue"
        >
          Continuar
        </CustomButton>
      </div>
    </div>
  );
}
